<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-archive-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <div v-else class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("meal") }} {{ sortArrow("name") }}</button>
            </th>
            <th
              v-for="shift in visibleShifts"
              :key="shift.id"
              scope="col"
              class="is-right"
              :aria-sort="ariaSort(shift.key)"
            >
              <button type="button" @click="sortBy(shift.key)">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                {{ sortArrow(shift.key) }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('total')">
              <button type="button" @click="sortBy('total')">{{ $t("total") }} {{ sortArrow("total") }}</button>
            </th>
            <th scope="col" class="is-right rd-col-actions"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row.key"
            data-open
            tabindex="0"
            :class="{ 'is-selected': isOpen(row) }"
            @click="openWorkers(row, $event)"
            @keydown.enter.prevent="openWorkers(row, $event)"
            @keydown.space.prevent="openWorkers(row, $event)"
          >
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td
              v-for="shift in visibleShifts"
              :key="shift.id"
              class="is-right rd-num"
              :class="{ 'is-zero': !row[shift.key] }"
            >
              {{ row[shift.key] }}
            </td>
            <td class="is-right rd-num"><b>{{ row.total }}</b></td>
            <td class="is-right">
              <div class="rd-tbl__actions">
                <button type="button" class="rd-btn rd-btn--sm" :aria-label="$t('employees')" @click.stop="openWorkers(row, $event)">
                  <v-icon size="16">mdi-account-multiple-outline</v-icon>
                  <span class="rd-btn__label">{{ $t("employees") }}</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">{{ totals[shift.key] }}</td>
            <td class="is-right rd-num">{{ totals.total }}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyReportTabReservedMeals from "@/components/company/reports/companyTabs/companyReportTabReservedMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import { hasPeriodRefetch } from "../../hasPeriodRefetch";
import CompanyDesktopReservedWorkers from "./CompanyDesktopReservedWorkers.vue";

/**
 * Rezervni obroci firme na računaru: obrok × smena; klik na red (ili "Radnici") otvara u panelu radnike koji su
 * obrok naručili. Firme, period i pretragu čita iz zajedničke trake. Nasleđuje učitavanje iz starog taba
 * (koji nema štampu ni Excel).
 */
export default {
  name: "CompanyDesktopReserved",
  extends: CompanyReportTabReservedMeals,
  mixins: [hasTableSort, hasPeriodRefetch("fetchReservedMeals")],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  computed: {
    visibleShifts() {
      return SHIFTS.filter((shift) => shift.id < 4 || this.meals.some((meal) => meal[shift.key] > 0));
    },
    rows() {
      const found = this.search ? this.customSearch(this.meals, [{ value: "name" }], this.search) : this.meals;
      return this.sortRows(
        found
          .filter((meal) => meal.total > 0)
          .map((meal, index) => {
            const row = { key: `${meal.id || meal.name}-${index}`, source: meal, name: this.toLatin(meal.name), total: meal.total };
            SHIFTS.forEach((shift) => {
              row[shift.key] = meal[shift.key] || 0;
            });
            return row;
          })
      );
    },
    totals() {
      const totals = { total: this.rows.reduce((sum, row) => sum + row.total, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + row[shift.key], 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.search
        ? this.$t("report_mobile.no_results_for", { search: this.search })
        : this.$t("no_reserved_meals_data");
    },
    emptyText() {
      return this.search ? "" : this.$t("select_date_and_company");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("r_o"), value: this.totals.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.rows.length },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading: {
      handler(loading) {
        this.$emit("loading", loading);
      },
      immediate: true,
    },
  },
  methods: {
    workersKey(row) {
      return `reserved-${row.source.id || row.name}`;
    },
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === this.workersKey(row));
    },
    /** Radnici koji su naručili obrok, u panelu. */
    openWorkers(row, event) {
      this.$emit("open-panel", {
        opener: event && event.currentTarget,
        key: this.workersKey(row),
        root: true,
        title: row.name,
        subtitle: this.$t("employees_who_ordered"),
        component: CompanyDesktopReservedWorkers,
        props: {
          meal: row.source,
          companyIds: this.$store.state.report.selectedFoodCompanies.map((company) => company.id),
          from: this.dates.from,
          to: this.dates.to,
        },
      });
    },
    async copyTable() {
      const headers = [this.$t("meal"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.rows.map((row) => [row.name, ...this.visibleShifts.map((shift) => row[shift.key]), row.total]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
